// "Worst offenders" — how the Insights (crash by crash) and Cluster insights navigators
// rank what they step through. A crash scores its KABCO comprehensive cost and the number
// of deficiencies at its location. A cluster scores its deficiencies — points per run by
// the run's severity and per analysis involved, each set separately (Yaron, 2026-09-29:
// severe 25,000 · moderate 10,000 · minor 5,000 · every analysis 50,000; scaled down by 1,000 on
// 2026-10-25: 25 · 10 · 5 · 50, crash costs counted in $1,000s — the same order, smaller numbers) —
// and, once crash data is loaded, the crash cost on it and its correlated crashes. The Safety Overview lets
// the user set the weights (Settings -> Worst offenders); the Safety Dashboard keeps the
// clusters' built-in score (safetyFilters.clusterDeficiencies' worstScore).
import { kabcoOf } from './insightsText';

export const OFFENDERS_KEY = 'safetyOverview:offenders';
export const OFFENDER_ANALYSES = ['SSD', 'PSD', 'GLR', 'FSA', 'HCA', 'GRW', 'ISD'];
export const OFFENDER_SEVERITIES = ['severe', 'moderate', 'minor'];
// every weight is in points; a crash cost counts in $1,000s (cost weight 1 = 1 point per $1,000)
export const WEIGHTS_VERSION = 2;
export const DEFAULT_OFFENDER_WEIGHTS = {
  crash: { cost: 1, perFinding: 1 },
  cluster: {
    severity: { severe: 25, moderate: 10, minor: 5 },
    analysis: Object.fromEntries(OFFENDER_ANALYSES.map((a) => [a, 50])),
    cost: 1,
    correlatedCrash: 25,
  },
};
const K = 1000;          // dollars per cost point

const num = (v, d) => (Number.isFinite(Number(v)) && Number(v) >= 0 ? Number(v) : d);

export function normalizeWeights(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const d = DEFAULT_OFFENDER_WEIGHTS;
  const rc = r.cluster || {};
  // weights a browser saved before 2026-10-25 are 1,000 times larger: scale them down (same order)
  const old = raw && typeof raw === 'object' && !(r.v >= WEIGHTS_VERSION);
  const sc = (v) => (old && Number.isFinite(Number(v)) ? Number(v) / K : v);
  // an older browser kept one number for every analysis: it now seeds each of them
  const perAnalysis = typeof rc.analysis === 'object' && rc.analysis ? rc.analysis
    : Object.fromEntries(OFFENDER_ANALYSES.map((a) => [a, rc.analysis]));
  return {
    v: WEIGHTS_VERSION,
    crash: { cost: num(r.crash?.cost, d.crash.cost), perFinding: num(r.crash?.perFinding, d.crash.perFinding) },
    cluster: {
      severity: Object.fromEntries(OFFENDER_SEVERITIES.map((s) => [s, num(sc((rc.severity || {})[s]), d.cluster.severity[s])])),
      analysis: Object.fromEntries(OFFENDER_ANALYSES.map((a) => [a, num(sc(perAnalysis[a]), d.cluster.analysis[a])])),
      cost: num(rc.cost, d.cluster.cost),
      correlatedCrash: num(sc(rc.correlatedCrash), d.cluster.correlatedCrash),
    },
  };
}
export function loadWeights() {
  try { return normalizeWeights(JSON.parse(localStorage.getItem(OFFENDERS_KEY) || 'null')); } catch (e) { return normalizeWeights(null); }
}
export function saveWeights(w) {
  const n = normalizeWeights(w);
  try { localStorage.setItem(OFFENDERS_KEY, JSON.stringify(n)); } catch (e) { /* private window */ }
  return n;
}

export const crashScore = (c, costs = {}, w = DEFAULT_OFFENDER_WEIGHTS) =>
  ((costs[kabcoOf(c)] || 0) / K) * w.crash.cost + Object.keys(c.findings || {}).length * w.crash.perFinding;

export const clusterScore = (cl, w = DEFAULT_OFFENDER_WEIGHTS) => {
  const sev = cl.severityCounts || {};
  const keys = cl.analysisKeys || Object.keys(cl.analyses || {});
  return OFFENDER_SEVERITIES.reduce((s, k) => s + (sev[k] || 0) * w.cluster.severity[k], 0)
    + keys.reduce((s, a) => s + (w.cluster.analysis[a] ?? DEFAULT_OFFENDER_WEIGHTS.cluster.analysis.SSD), 0)
    + ((cl.cost || 0) / K) * w.cluster.cost + (cl.correlatedCount || 0) * w.cluster.correlatedCrash;
};

const fmt = (n) => Number(n).toLocaleString('en-US');
/** What "Worst offenders" means, in words (hover text). */
export const crashLogic = (w = DEFAULT_OFFENDER_WEIGHTS) =>
  `Worst offenders: each correlated crash scores ${fmt(w.crash.cost)} per $1,000 of its crash cost (the KABCO comprehensive cost of its injury severity)`
  + ` + ${fmt(w.crash.perFinding)} per deficiency found at its location. Highest score first; set the weights in Settings → Worst offenders.`;
export const clusterLogic = (w = DEFAULT_OFFENDER_WEIGHTS, withCrash = true) => {
  const a = w.cluster.analysis;
  const same = OFFENDER_ANALYSES.every((k) => a[k] === a.SSD);
  const perAnalysis = same ? `${fmt(a.SSD)} per analysis involved`
    : `per analysis involved ${OFFENDER_ANALYSES.map((k) => `${k} ${fmt(a[k])}`).join(', ')}`;
  return `Worst offenders: each cluster scores ${fmt(w.cluster.severity.severe)} per severe deficiency run, `
    + `${fmt(w.cluster.severity.moderate)} per moderate and ${fmt(w.cluster.severity.minor)} per minor, + ${perAnalysis}`
    + (withCrash ? `; with crash data also ${fmt(w.cluster.cost)} per $1,000 of crash cost on it + ${fmt(w.cluster.correlatedCrash)} per correlated crash.` : '.')
    + ' Highest score first; set the weights in Settings → Worst offenders.';
};

/** A deficiency site's score — the rank the map's Top N and the sites table share: the points of
 *  its severity and of its analysis (the cluster weights), and once crash data is loaded the crash
 *  cost on the site and its correlated crashes. crash = {cost, correlated} of the crashes at it. */
export const siteScore = (site, crash = {}, w = DEFAULT_OFFENDER_WEIGHTS) =>
  (w.cluster.severity[site.severity] ?? w.cluster.severity.minor)
  + (w.cluster.analysis[site.analysis] ?? DEFAULT_OFFENDER_WEIGHTS.cluster.analysis.SSD)
  + ((crash.cost || 0) / K) * w.cluster.cost + (crash.correlated || 0) * w.cluster.correlatedCrash;

/** How a site's score is made, part by part (the worked examples: the score dialog and the PDF). */
export const siteScoreParts = (site, crash = {}, w = DEFAULT_OFFENDER_WEIGHTS) => {
  const severity = w.cluster.severity[site.severity] ?? w.cluster.severity.minor;
  const analysis = w.cluster.analysis[site.analysis] ?? DEFAULT_OFFENDER_WEIGHTS.cluster.analysis.SSD;
  const crashCost = ((crash.cost || 0) / K) * w.cluster.cost;
  const correlated = (crash.correlated || 0) * w.cluster.correlatedCrash;
  return { severity, analysis, crashCost, correlated, total: severity + analysis + crashCost + correlated };
};

/** A hotspot's or prospect's score, site by site: each site's points times the share of it inside the stretch. */
export const stretchBreakdown = (stretch, w = DEFAULT_OFFENDER_WEIGHTS) => {
  const rows = (stretch.parts || []).map(({ site, share }) => {
    const parts = siteScoreParts(site, {}, w);
    return { site, share, ...parts, points: parts.total * share };
  }).sort((a, b) => (a.site.mpStart ?? 0) - (b.site.mpStart ?? 0));
  return { rows, total: rows.reduce((t, r) => t + r.points, 0) };
};

export const siteLogic = (w = DEFAULT_OFFENDER_WEIGHTS, withCrash = true) =>
  `Rank: each deficiency site scores ${fmt(w.cluster.severity.severe)} if severe, ${fmt(w.cluster.severity.moderate)} if moderate, `
  + `${fmt(w.cluster.severity.minor)} if minor, + its analysis' weight`
  + (withCrash ? `; with crash data also ${fmt(w.cluster.cost)} per $1,000 of crash cost at it + ${fmt(w.cluster.correlatedCrash)} per correlated crash` : '')
  + `. Ties: ISD the larger share of rays blocked, the others the longer site first. Set the weights in Settings → Worst offenders.`;
